import React from 'react';
import { fmtDur, firstReplyLatency } from './memberEngagementUtils';
import { STATUS_DESCRIPTION, statusLabel } from '../../utils/memberStatus';

// Shared presentation helpers for the Member Engagement UI. Extracted so the
// list page and the reusable drawer (used by the Subscription Report) render
// pills, tags and message bubbles identically.

export const subPill = (st) => {
  // `expired` is its own state (the Subscription Report's `exp`), distinct from
  // past-due: the term simply ran out. Expired subscribers now appear on this page
  // because they are on the canonical subscriber list.
  const map = {
    active: ['active', 'Active'],
    cancels: ['cancels', 'Cancels'],
    expired: ['past', 'Expired'],
    past_due: ['past', 'Past due'],
  };
  const a = map[st] || map.active;
  return <span className={`sub-pill ${a[0]}`}>{a[1]}</span>;
};

export const acctPill = (st) => (
  <span className={`acct-pill ${st}`} title={STATUS_DESCRIPTION[st]}><i />{statusLabel(st)}</span>
);

// The avatar dot is PRESENCE — connected right now — and nothing else. It is NOT
// account status: a deleted / suspended / deactivated member simply has no dot, the
// same as anyone else who is offline. Account status is its own column (the
// DELETED tag on the name, and the drawer's "Account" pill). Conflating the two is
// WEB-68, where a member who had deleted his profile still wore a green dot.
export const onlineDot = () => <span className="adot online" title="Online now" />;

// Member avatar: shows the real profile photo when available, otherwise the
// initials avatar. `online` (optional) overlays the green presence dot; offline
// members get no dot at all. The photo is an <img> layered over the initials so a
// missing/purged image (e.g. a deleted member whose file is gone) falls back to
// the initials via onError instead of rendering a blank box.
export const Avatar = ({ src, initials, online = false, className = '' }) => (
  <span className={`ava ${className}`.trim()}>
    {initials}
    {src && (
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
      />
    )}
    {online && onlineDot()}
  </span>
);

export const statusPill = (s) => {
  if (s === 'good') return <span className="pill good"><i />Getting replies</span>;
  if (s === 'warn') return <span className="pill warn"><i />Low response</span>;
  return <span className="pill risk"><i />At risk</span>;
};

export const convTag = (c) => {
  // INBOUND thread — the member never spoke in it, so the missing reply is THEIRS,
  // not the match's. `firstReplyLatency` is null here for the same reason it is null
  // on an unanswered outbound thread, so without this branch two opposite findings
  // ("nobody answered them" vs "they answered nobody") would wear the same tag.
  if (c.inbound) return <span className="tag ignored">Member never replied</span>;
  const l = firstReplyLatency(c);
  if (l === null) return <span className="tag none">No reply</span>;
  if (l > 720) return <span className="tag slow">{`Replied in ${fmtDur(l)}`}</span>;
  return <span className="tag replied">{`Replied in ${fmtDur(l)}`}</span>;
};

export const SearchIcon = () => (
  <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
);

export const Chevron = () => (
  <svg className="chev" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
);

// Non-text message types → icon + label, so media/requests don't render as
// raw text (the chat stores a placeholder like "video" in the message field).
const MEDIA_LABELS = {
  image: ['🖼️', 'Photo'],
  video: ['🎥', 'Video'],
  audio: ['🎙️', 'Voice message'],
  file: ['📎', 'File'],
  location: ['📍', 'Location'],
  view_once_image: ['🖼️', 'View-once photo'],
  view_once_video: ['🎥', 'View-once video'],
  view_once_audio: ['🎙️', 'View-once audio'],
  view_once_opened: ['👁️', 'View-once opened'],
  pic_request: ['🔒', 'Private photo request'],
  pic_request_accept: ['✅', 'Private photo accepted'],
  pic_request_reject: ['⛔', 'Private photo declined'],
  profile_pic_request: ['🖼️', 'Profile photo request'],
  call_request: ['📞', 'Call request'],
  call_request_accept: ['📞', 'Call accepted'],
  call_request_reject: ['📞', 'Call declined'],
};

export const renderMsgContent = (msg) => {
  const mt = msg.mt || 'text';
  if (mt === 'text') return <span>{msg.x}</span>;
  const lbl = MEDIA_LABELS[mt];
  if (!lbl) return <span>{msg.x || mt}</span>;
  const txt = (msg.x || '').trim();
  const hasCaption = txt && txt.toLowerCase() !== mt && txt.toLowerCase() !== lbl[1].toLowerCase();
  return (
    <span className="msg-media">
      <span className="mi-ico">{lbl[0]}</span>{lbl[1]}{hasCaption ? `: ${msg.x}` : ''}
    </span>
  );
};

// Profile popup styling — mirrors the user-management "View" modal.
export const profileModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 1300,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: '0 30px 30px 30px',
  maxHeight: '80vh',
  overflow: 'auto',
};
